import { createRef, useMemo } from 'react';
import { FilePond } from 'react-filepond';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import {
    faCircleNotch,
    faDownload,
    faEye,
    faFileCircleXmark,
    faPlus,
    faQuestionCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchLegalEntityFiles, uploadLegalEntityFile } from 'api/verification';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip/Tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import prettyBytes from 'pretty-bytes';
import { accountQueryKeys } from 'queries/account-query-keys';
import { LegalEntityFile } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<LegalEntityFile>();

const NonProfitVerificationFiles = () => {
    const ref = createRef<FilePond>();
    const { accountId } = useCurrentAccount();
    const toast = useToast();
    const queryClient = useQueryClient();
    const { pagination, setPagination } = Table.useReactTableState();
    const [isPending, params] = useIsPending({
        page: pagination.pageIndex + 1,
    });

    const { data: files } = useSuspenseQuery({
        queryKey: accountQueryKeys.legalEntityFilesWithParams(accountId, params),
        queryFn: () => fetchLegalEntityFiles(accountId, params),
    });

    const { mutate: uploadFile, isPending: isLoading } = useMutation({
        mutationFn: (formData: FormData) => {
            return uploadLegalEntityFile(accountId, formData);
        },
        onSuccess: () => {
            toast.success('Successfully uploaded verification file.');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.legalEntityFilesWithParams(accountId, params),
            });
            ref.current?.removeFiles();
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            toast.error(data?.message ?? 'Failed to upload verification file.');
        },
    });

    const columns = useMemo(() => {
        return [
            columnHelper.accessor('name', {
                header: 'File Name',
                cell: info => {
                    const file = info.row.original;
                    return (
                        <a
                            className="max-w-60 truncate text-nowrap text-gray-900 hover:text-gray-900"
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {file.name}
                        </a>
                    );
                },
            }),
            columnHelper.accessor('size', {
                header: 'Size',
                cell: info => {
                    const { size } = info.row.original;

                    return prettyBytes(size ?? 0);
                },
            }),
            columnHelper.accessor('mime_type', {
                header: 'File Type',
                cell: info => (
                    <span className="max-w-52 truncate text-nowrap">
                        {info.row.original.mime_type}
                    </span>
                ),
            }),
            columnHelper.accessor('created_at', {
                header: 'Uploaded At',
                cell: info => (
                    <span className="max-w-52 truncate text-nowrap">
                        {isoDateFromServer(info.row.original.created_at).toLocaleString(
                            DateTime.DATE_FULL,
                        )}
                    </span>
                ),
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const file = info.row.original;

                    return (
                        <DropdownMenu.Root key={file.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content>
                                <DropdownMenu.AnchorItem
                                    href={file.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <div className="flex items-center gap-1">
                                        <FontAwesomeIcon icon={faEye} />
                                        View
                                    </div>
                                </DropdownMenu.AnchorItem>
                                <DropdownMenu.AnchorItem
                                    href={file.download_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <div className="flex items-center gap-1">
                                        <FontAwesomeIcon icon={faDownload} />
                                        Download
                                    </div>
                                </DropdownMenu.AnchorItem>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ];
    }, []);

    const table = useReactTable({
        data: files.data,
        columns,
        pageCount: files.meta.last_page,
        getCoreRowModel: getCoreRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    const handleFileChange = (file: File) => {
        const formData = new FormData();
        formData.append('files[]', file, file.name);
        uploadFile(formData);
    };

    useSyncPaginatedData(table, files);

    return (
        <Box className="overflow-hidden">
            <div className={'relative flex items-center justify-between px-6 py-4'}>
                <div className={'flex items-center gap-2'}>
                    <p className={'text-lg font-medium text-gray-900'}>Files</p>
                    <Tooltip.Root>
                        <Tooltip.Trigger asChild={true} className={`cursor-help text-gray-500`}>
                            <FontAwesomeIcon icon={faQuestionCircle} />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content inverse>
                                <div className="flex flex-col gap-1">
                                    <p>Maximum upload file size: 5MB</p>
                                    <p>Accepted file types: .doc, .docx, .pdf, .jpg, .jpeg, .png</p>
                                </div>
                                <Tooltip.Arrow inverse />
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
                <div className="invisible h-0">
                    <FilePond
                        ref={ref}
                        allowImagePreview={false}
                        maxFileSize={'5MB'}
                        allowMultiple
                        acceptedFileTypes={['.doc', '.docx', '.pdf', '.png', '.jpg', '.jpeg']}
                        onaddfile={(_, file) => {
                            !file.getMetadata('id') && handleFileChange(file.file as File);
                        }}
                    />
                </div>
                <Button
                    size={'sm'}
                    variant={'outline'}
                    color={'neutral'}
                    disabled={isLoading}
                    onClick={() => ref.current?.browse()}
                >
                    <FontAwesomeIcon spin={isLoading} icon={isLoading ? faCircleNotch : faPlus} />
                    <span>{isLoading ? 'Uploading' : 'Upload'}</span>
                </Button>
            </div>
            <Separator />
            <Table.Root isLoading={isPending} variant={'bordered'} table={table}>
                <Table.Content>
                    <Table.Header />
                    <Table.Body />
                </Table.Content>
                <Table.Empty variant={'secondary'}>
                    <Table.EmptyIcon>
                        <FontAwesomeIcon icon={faFileCircleXmark} />
                    </Table.EmptyIcon>
                    <Table.EmptyTitle>No files found</Table.EmptyTitle>
                </Table.Empty>
                <Table.Pagination>
                    <Table.PaginationPageNavigation />
                </Table.Pagination>
            </Table.Root>
        </Box>
    );
};

export { NonProfitVerificationFiles };
